'use client';
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { User, Session } from '@supabase/supabase-js';

interface UserProfile {
  id: string
  name?: string
  email?: string
  [key: string]: any
}

interface AuthContextType {
  user: User | null
  userProfile: UserProfile | null
  loading: boolean
  profileLoading: boolean
  signIn: (email: string, password: string) => Promise<any>
  signOut: () => Promise<any>
  updateProfile: (updates: Partial<UserProfile>) => Promise<any>
  isAuthenticated: boolean
  sessionStatus: 'loading' | 'authenticated' | 'unauthenticated'
}

const AuthContext = createContext<AuthContextType | null>(null)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [sessionStatus, setSessionStatus] = useState<'loading' | 'authenticated' | 'unauthenticated'>('loading')

  const profileOperations = {
    async load(userId: string) {
      if (!userId || !supabase) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', userId)
          .single()
        if (!error) setUserProfile(data)
      } catch (error) {
        // Profile loading errors are non-critical
      } finally {
        setProfileLoading(false)
      }
    },
    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    },
  }

  const authStateHandlers = {
    onChange: (_event: string | null, session: Session | null) => {
      const currentUser = session?.user ?? null
      setUser(currentUser)
      setLoading(false)
      
      // Update session status
      if (currentUser) {
        setSessionStatus('authenticated')
        profileOperations.load(currentUser.id)
      } else {
        setSessionStatus('unauthenticated')
        profileOperations.clear()
      }
    },
  }

  useEffect(() => {
    // Check if Supabase client is properly initialized
    if (!supabase) {
      console.warn('Supabase client not initialized. Please configure environment variables.')
      setLoading(false)
      setSessionStatus('unauthenticated')
      return
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      authStateHandlers.onChange(null, session)
    }).catch((error) => {
      console.error('Failed to get session:', error)
      setLoading(false)
      setSessionStatus('unauthenticated')
    })

    // Subscribe to auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(authStateHandlers.onChange)

    return () => subscription?.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    if (!supabase) {
      return { 
        data: null, 
        error: { message: 'Supabase not configured. Please contact administrator.' } 
      }
    }
    
    setLoading(true)
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      })
      
      if (error) {
        setLoading(false)
        return { data: null, error }
      }
      
      // Don't set loading to false here - let onAuthStateChange handle it
      // This ensures proper state synchronization
      return { data, error: null }
    } catch (error: any) {
      setLoading(false)
      
      // Handle network errors specifically
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('AuthRetryableFetchError')) {
        return { 
          data: null, 
          error: { 
            message: 'Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.' 
          } 
        }
      }
      
      return { 
        data: null, 
        error: { message: 'Network error. Please try again.' } 
      }
    }
  }

  const signOut = async () => {
    if (!supabase) {
      return { error: { message: 'Supabase not configured.' } }
    }
    
    try {
      const { error } = await supabase.auth.signOut()
      if (!error) {
        setUser(null)
        setSessionStatus('unauthenticated')
        profileOperations.clear()
      }
      return { error }
    } catch (error: any) {
      if (error?.message?.includes('Failed to fetch') || 
          error?.message?.includes('NetworkError')) {
        return { 
          error: { 
            message: 'Cannot connect to authentication service. You may already be logged out.' 
          } 
        }
      }
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) return { error: { message: 'No user logged in' } }
    if (!supabase) {
      return { error: { message: 'Supabase not configured.' } }
    }
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error: any) {
      if (error?.message?.includes('Failed to fetch')) {
        return { 
          error: { 
            message: 'Cannot connect to database. Please check your connection.' 
          } 
        }
      }
      return { error: { message: 'Failed to update profile. Please try again.' } }
    }
  }

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    sessionStatus,
  }

  return <AuthContext.Provider data-component-id="src/contexts/AuthContext.tsx:214:9" data-component-path="src/contexts/AuthContext.tsx" data-component-line="214" data-component-file="AuthContext.tsx" data-component-name="AuthContext.Provider" data-component-content="%7B%22elementName%22%3A%22AuthContext.Provider%22%2C%22value%22%3A%22%5Bvar%3Avalue%5D%22%7D" value={value}>{children}</AuthContext.Provider>
}